// @flow
import { PDFFillerContext } from 'pages/Contexts/PdfFillerContext'
import * as React from 'react'
import Button from 'ui/atoms/Button'
import Heading from 'ui/atoms/Heading'
import Space from 'ui/atoms/Space'
import Dropdown from 'ui/Dropdown'
import TextField from 'ui/molecules/TextField'
import ToggleField from 'ui/molecules/ToggleField'

import { useLoadDemographicsIntegration } from '@/queries/integrations/demographics/useLoadDemographicsIntegration'

import InputSettingsPanelFontSizeField from './InputSettingsPanelFontSizeField'

function InputSettingsPanel(): React.Node {
  const {
    inputFields,
    selectedInputFieldId,
    setInputFields,
    setRemovedInputFields,
    setSelectedInputFieldId,
  } = React.useContext(PDFFillerContext)
  const { data: demographics } = useLoadDemographicsIntegration()

  const handleInputChange = React.useCallback(
    (key: string, value) => {
      if (!selectedInputFieldId) return

      setInputFields((prevInputFields) => {
        return {
          ...prevInputFields,
          [selectedInputFieldId]: {
            ...prevInputFields[selectedInputFieldId],
            [key]: value,
          },
        }
      })
    },
    [selectedInputFieldId, setInputFields]
  )
  const handleRequiredChange = React.useCallback(
    (event: SyntheticInputEvent<HTMLInputElement>) => {
      handleInputChange('required', event.currentTarget.checked)
    },
    [handleInputChange]
  )

  const getFirstSignatureInput = React.useCallback(() => {
    const signatureInput = Object.values(inputFields)?.find((inputField) => {
      return !inputField.is_office_use && inputField.input_type === 'signature'
    })

    if (!signatureInput) return null

    return signatureInput.id
  }, [inputFields])

  const handleLinkedSignatureChange = React.useCallback(
    (event: SyntheticInputEvent<HTMLInputElement>) => {
      handleInputChange(
        'linked_signature',
        event.currentTarget.checked ? getFirstSignatureInput() : null
      )
    },
    [getFirstSignatureInput, handleInputChange]
  )

  const handleDemographicsChange = React.useCallback(
    (value) => {
      handleInputChange('demographics', value)
    },
    [handleInputChange]
  )

  const selectedInputField = React.useMemo(() => {
    if (!selectedInputFieldId) return null

    return inputFields[selectedInputFieldId]
  }, [inputFields, selectedInputFieldId])

  const handleDeleteInput = React.useCallback(async () => {
    if (!selectedInputFieldId) return

    setInputFields((prevInputFields) => {
      delete prevInputFields[selectedInputFieldId]

      return {
        ...prevInputFields,
      }
    })

    setRemovedInputFields((prevRemovedInputObjects) => [
      ...prevRemovedInputObjects,
      selectedInputFieldId,
    ])

    setSelectedInputFieldId(null)
  }, [
    selectedInputFieldId,
    setInputFields,
    setRemovedInputFields,
    setSelectedInputFieldId,
  ])

  const hasSignatureInputs = React.useMemo(() => {
    return Object.values(inputFields).some(
      (inputField) => inputField.input_type === 'signature'
    )
  }, [inputFields])

  const handleOfficeUseToggle = React.useCallback(
    (event) => {
      handleInputChange('is_office_use', event.target.checked)
    },
    [handleInputChange]
  )

  const demographicsOptions = React.useMemo(() => {
    if (selectedInputField?.input_type === 'full_name') {
      return (
        demographics?.filter(
          (option) =>
            !(
              option.value.includes('first_name') ||
              option.value.includes('middle_name') ||
              option.value.includes('last_name')
            )
        ) || []
      )
    }
    return (
      demographics?.filter((option) => !option.value.includes('full_name')) ||
      []
    )
  }, [demographics, selectedInputField?.input_type])

  const handleClientSignatureChange = React.useCallback(
    (event) => {
      handleInputChange('client_signature', event.currentTarget.checked)
    },
    [handleInputChange]
  )

  if (!selectedInputField) return null

  return (
    <Space size="xlarge">
      <Space size="large">
        <Heading>Field settings</Heading>
        <Space>
          {['date', 'text', 'full_name'].includes(
            selectedInputField.input_type
          ) && <InputSettingsPanelFontSizeField />}
          {['text'].includes(selectedInputField.input_type) && (
            <TextField
              semibold
              label="Placeholder"
              onChange={(value) => handleInputChange('placeholder', value)}
              value={selectedInputField.placeholder}
            />
          )}
          <ToggleField
            horizontal
            primary
            smallLabel
            label="Required "
            onChange={handleRequiredChange}
            value={selectedInputField.required}
          />
          {selectedInputField.input_type === 'date' && (
            <ToggleField
              horizontal
              primary
              smallLabel
              disabled={!hasSignatureInputs}
              label="Link to signature"
              onChange={handleLinkedSignatureChange}
              value={Boolean(selectedInputField.linked_signature)}
            />
          )}
          <ToggleField
            horizontal
            primary
            smallLabel
            label="Office Use"
            onChange={handleOfficeUseToggle}
            value={selectedInputField.is_office_use}
          />
          {['signature', 'initials'].includes(
            selectedInputField.input_type
          ) && (
            <ToggleField
              horizontal
              primary
              smallLabel
              label="Client Signature"
              onChange={handleClientSignatureChange}
              value={Boolean(selectedInputField.client_signature)}
            />
          )}
          {['date', 'text', 'full_name'].includes(
            selectedInputField.input_type
          ) && (
            <Dropdown
              label="Client Mapping"
              onChange={handleDemographicsChange}
              options={demographicsOptions}
              placeholder="Client Mapping"
              value={selectedInputField?.demographics}
            />
          )}
        </Space>
      </Space>
      <Button $block $danger onClick={handleDeleteInput}>
        Delete Field
      </Button>
    </Space>
  )
}

export default InputSettingsPanel
